import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Container, Grid, Paper, Stack, Typography, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Button } from "@mui/material";
import ScreenBanner from "../../../../../components/ScreenBanner";
import useSetParamsURL from "../../../../../lib/useSetParamsURL";
import { useQuery } from "react-query";
import axiosClient from "../../../../../lib/axios";
import DisplayNotFindInfo from "../../../../../components/DisplayNotFindInfo";
import { useModalDetalheProduto } from "../ModalDetalheProdutoContext";
import DialogProdutoFilter from "./components/DialogProdutoFilter";
import { IDataDepartamentosEstoque } from "../../../../../types/api/estoque/pesquisar-departamentos-estoque";
import { IDataSetores } from "../../../../../types/api/estoque/pesquisar-setores";
import useDebounce from "../../../../../lib/useDebounce";
import SearchWithFilters from "../../../../../components/BasicComponents/SearchWithFilters";
import ChipList from "../../../../../components/BasicComponents/ChipList";
import { getChipFilterItems } from "../../../../../helpers/getChipFilterItems";
import { TCategoriaEstoque } from "../../../../../components/SelectsSimpleApiQuery/SelectEstoqueCategorias";
import { processUrlFilters } from "../../../../../components/TableDataGridV2/helpers";
import { TFilters } from "../../../../../components/TableDataGridV2/types";
import { IPesquisaTributacao } from "../../../../../components/SelectsSimpleApiQuery/SelectTributacao";
import SkeletonListagemProdutos from "./SkeletonListagemProdutos";

export default function Listagem() {

    const { openModal } = useModalDetalheProduto();
    const { setParamsUrl, searchParams, setMultipleParamsUrl } = useSetParamsURL();

    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const [filtros, setFiltros] = useState<TFiltrosProduto>({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openFiltros, setOpenFiltros] = useState(false);
    const deboucedSearchTerm = useDebounce(filtros.palavraChave ?? '', 1000);

    const filters = useMemo(() => {
        return {
            departamento_id_contem: filtros.departamento ? filtros.departamento.map((dpto) => dpto.id) : null,
            setor_id_contem: filtros.setor ? filtros.setor.map((setor) => setor.id) : null,
            tributacao: filtros.tributacao ? filtros.tributacao.nome : null,
            tipo_contem: filtros.tipo && filtros.tipo.length > 0 ? filtros.tipo : null,
            categoria_contem: filtros.categoria && filtros.categoria.length > 0 ? filtros.categoria.map((cat) => cat.id) : null,
            pesquisar: deboucedSearchTerm,
        }
    }, [filtros, deboucedSearchTerm])

    // Querys e Mutations
    const { data, isFetching } = useQuery(
        ["estoque-produtos", page, rowsPerPage, filters],
        async () => {
            const params = {
                page: page + 1,
                limit: rowsPerPage,
                ...filters
            };
            const res = await axiosClient.get("/estoque/pesquisar-produtos", { params });
            return res.data;
        },
        { keepPreviousData: true }
    );

    const produtos = data?.data || [];

    const total = data?.total || 0;

    // Funções manipuladoras
    const handleChangePage = (_: any, newPage: number) => {
        setPage(newPage);
    };

    const handleSearch = useCallback((pesquisa: string) => {
        const urlFilters = processUrlFilters({}, searchParams);
        urlFilters.pesquisar = pesquisa || undefined;
        setMultipleParamsUrl(urlFilters);
        setFiltros((prevState) => ({ ...prevState, palavraChave: pesquisa }));
        setPage(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const handleAplyFilters = (newFilters: TFiltrosProduto) => {
        const novoObjeto: TFilters = { ...newFilters };
        Object.keys(newFilters).forEach((key) => {
            if (key.startsWith('departamento')) {
                novoObjeto.departamento_id_contem = [novoObjeto[key].map((dpto: IDataDepartamentosEstoque) => dpto.id)];
                delete novoObjeto[key];
            }
            if (key.startsWith('categoria')) {
                novoObjeto.categoria_contem = [novoObjeto[key].map((cat: TCategoriaEstoque) => cat.id)];
                delete novoObjeto[key];
            }
            if (key.startsWith('setor')) {
                novoObjeto.setor_id_contem = [novoObjeto[key].map((setor: IDataSetores) => setor.id)];
                delete novoObjeto[key];
            }
            if (key.startsWith('tipo')) {
                novoObjeto.tipo_contem = [novoObjeto[key].map((tipo: string) => tipo)];
                delete novoObjeto[key];
            }
            if(key.startsWith('tributacao')) {
                novoObjeto.tributacao = novoObjeto[key]?.nome;
            }
            if (key.startsWith('palavraChave')) {
                novoObjeto.pesquisar = novoObjeto.palavraChave;
                delete novoObjeto[key];
            }
        });

        setMultipleParamsUrl(novoObjeto);

        setFiltros(newFilters);
        setPage(0);
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const chipItems = useMemo(() => {
        return getChipFilterItems(
            filtros,
            filtersChipLabels,
            ['palavraChave']
        );
    }, [filtros]);

    const handleDeleteFilter = (item: string) => {
        const newFiltros = { ...filtros };
        const urlFilters = processUrlFilters({}, searchParams);

        if (item.startsWith('Departamento:')) {
            delete newFiltros.departamento;
            delete urlFilters.departamento_id_contem;
        } else if (item.startsWith('Setor:')) {
            delete newFiltros.setor;
            delete urlFilters.setor_id_contem;
        } else if (item.startsWith('Tributação:')) {
            delete newFiltros.tributacao;
            delete urlFilters.tributacao;
        } else if (item.startsWith('Tipo:')) {
            delete newFiltros.tipo;
            delete urlFilters.tipo_contem;
        } else if (item.startsWith('Categoria:')) {
            delete newFiltros.categoria;
            delete urlFilters.categoria_contem;
        }
        else {
            Object.entries(filtros).forEach(([key, value]) => {
                const label = filtersChipLabels[key as keyof typeof filtersChipLabels] || key;
                const displayValue = Array.isArray(value) ? value.join(', ') : value;
                const chipText = `${label}: ${displayValue}`;

                if (chipText === item) {
                    delete newFiltros[key as keyof TFiltrosProduto];
                }
            });
        }

        setMultipleParamsUrl(urlFilters);
        setFiltros(newFiltros);
    }

    useEffect(() => {
        const urlFilters = processUrlFilters({}, searchParams);
        if (urlFilters && Object.keys(urlFilters).length > 0) {
            const filtrosFromUrl: TFiltrosProduto = {};
            if (urlFilters.pesquisar) {
                filtrosFromUrl.palavraChave = urlFilters.pesquisar;
            }
            if (urlFilters.departamento_id_contem) {
                filtrosFromUrl.departamento = Array.isArray(urlFilters.departamento_id_contem)
                    ? urlFilters.departamento_id_contem.map((id: number) => ({ id, nome: "" }))
                    : [];
            }
            if (urlFilters.setor_id_contem) {
                filtrosFromUrl.setor = Array.isArray(urlFilters.setor_id_contem)
                    ? urlFilters.setor_id_contem.map((id: number) => ({ id, nome: "" }))
                    : [];
            }
            if (urlFilters.tipo_contem) {
                filtrosFromUrl.tipo = Array.isArray(urlFilters.tipo_contem)
                    ? urlFilters.tipo_contem
                    : [urlFilters.tipo_contem];
            }
            if (urlFilters.categoria_contem) {
                filtrosFromUrl.categoria = Array.isArray(urlFilters.categoria_contem)
                    ? urlFilters.categoria_contem.map((id: number) => ({ id, categoria_id: id, nome: "", pai: null, atributos: [], ordem: [] }))
                    : [];
            }
            if (urlFilters.tributacao) {
                filtrosFromUrl.tributacao = { id: '0', nome: urlFilters.tributacao };
            }
            setFiltros(filtrosFromUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    return (
        <>
            <ScreenBanner title="Estoque" />

            <Box sx={{ background: "#F5F6FA", minHeight: "100vh", py: 4 }}>
                <Container maxWidth="xl">
                    <Typography textAlign="center" variant="h5" mb={3}>
                        Produtos
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }} ref={containerRef}>
                            <Paper elevation={2} sx={{ p: 2 }}>
                                <Box sx={{ width: "100%", overflowX: "auto" }}>
                                    <SearchWithFilters
                                        palavraChave={filtros.palavraChave || ''}
                                        textFieldProps={{
                                            inputRef: inputRef,
                                            label: "Pesquisar",
                                            placeholder: "Pesquise por nome ou código",
                                            size: "medium",
                                        }}
                                        pesquisar={handleSearch}
                                        setTermoPesquisa={handleSearch}
                                        handleOpenFilters={setOpenFiltros}
                                    />

                                    <ChipList
                                        items={chipItems}
                                        onDelete={handleDeleteFilter}
                                    />
                                    {
                                        isFetching && <SkeletonListagemProdutos />
                                    }
                                    {

                                        !isFetching &&
                                        <>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell><b>Código/Nome</b></TableCell>
                                                        <TableCell><b>Departamento</b></TableCell>
                                                        <TableCell><b>Setor</b></TableCell>
                                                        <TableCell><b>Categoria</b></TableCell>
                                                        <TableCell><b>Tributação</b></TableCell>
                                                    </TableRow>
                                                </TableHead>

                                                <TableBody>
                                                    {produtos.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={6}>
                                                                <DisplayNotFindInfo label="Nenhum produto encontrado" />
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        produtos.map((produto: Produto) => (
                                                            <TableRow key={produto.id} hover>
                                                                <TableCell>
                                                                    <Button
                                                                        variant="text"
                                                                        sx={{
                                                                            color: "primary.main",
                                                                            textDecoration: "underline",
                                                                            fontWeight: "bold",
                                                                            fontSize: "1rem",
                                                                            padding: 0,
                                                                            minWidth: "unset"
                                                                        }}
                                                                        onClick={() => {
                                                                            setParamsUrl(produto.id.toString(), "detalhe-produto");
                                                                            openModal(produto.id.toString());
                                                                        }}
                                                                    >
                                                                        {produto.id || "-"}
                                                                    </Button>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ color: "#888", fontSize: "0.85rem", mt: 0.5 }}
                                                                    >
                                                                        {produto.nome ?? '-'}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Stack gap={0.5}>
                                                                        <Typography variant="body2" fontWeight={500}>
                                                                            {produto.departamento?.nome || "-"}
                                                                        </Typography>
                                                                    </Stack>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Stack gap={0.5}>
                                                                        <Typography variant="body2" fontWeight={500}>
                                                                            {produto.setor?.nome || "-"}
                                                                        </Typography>
                                                                    </Stack>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2">
                                                                        {produto.categorias.length > 0
                                                                            ? produto.categorias.map(getCategoriaPath).join(' - ')
                                                                            : '-'}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2">
                                                                        {produto.tipo_tributacao || "-"}
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    )}
                                                </TableBody>
                                            </Table>

                                            <Stack direction="row" justifyContent="flex-end">
                                                <TablePagination
                                                    component="div"
                                                    count={total}
                                                    page={page}
                                                    onPageChange={handleChangePage}
                                                    rowsPerPage={rowsPerPage}
                                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                                    labelRowsPerPage="Linhas por página"
                                                />
                                            </Stack>
                                        </>
                                    }
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
                {openFiltros &&
                    <DialogProdutoFilter
                        open={openFiltros}
                        onClose={() => setOpenFiltros(false)}
                        onConfirm={(filtros) => {
                            setOpenFiltros(false);
                            setPage(0);
                            handleAplyFilters(filtros);
                        }}
                        filtrosAtuais={filtros}
                    />}
            </Box>
        </>
    );
}

export type TFiltrosProduto = {
    pesquisar?: string;
    departamento?: IDataDepartamentosEstoque[];
    setor?: IDataSetores[];
    categoria?: TCategoriaEstoque[];
    tipo?: string[];
    palavraChave?: string;
    tributacao?: IPesquisaTributacao;
}
export interface CategoriaProduto {
    id: number;
    nome: string;
    pai: CategoriaProduto | null;
}
export interface Produto {
    id: number;
    nome: string;
    departamento: {
        id: number | string;
        nome: string;
    };
    setor: {
        id: number | string;
        nome: string;
    };
    unidade_medida: string;
    categorias: CategoriaProduto[];
    tipo_tributacao: string;
}

const filtersChipLabels: { [key in keyof Omit<TFiltrosProduto, 'palavraChave'>]: string } = {
    departamento: 'Departamento',
    setor: 'Setor',
    tipo: 'Tipo',
    tributacao: 'Tributação',
    categoria: 'Categoria',
};

function getCategoriaPath(categoria: { nome: string; pai: any }) {
    const nomes: string[] = [];
    let atual = categoria;
    while (atual) {
        nomes.unshift(atual.nome);
        atual = atual.pai;
    }
    return nomes.join(' > ');
}